// Styles
import '../../MapStyles/MapMenu.scss';

// Utils
import { capitalize, getFormattedDateTime } from '@utils';

// Libraries
import * as L from 'leaflet';
import { useEffect, useMemo, useState } from 'react';
import { Button, Form, InputGroup } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';

// Components
import { Icon, Text } from '@components';

// Store
import { useMapStore } from '../../MapStore/UseMapStore';

export function MapEditList({
  data,
  type,
  listData,
  listMarkers,
  listRefs,
  visible,
  setIsCreating,
}) {
  const setSelected = useMapStore((state) => state.setSelected);
  const setShowMore = useMapStore((state) => state.setShowMore);
  const setClickedEvent = useMapStore((state) => state.setClickEvent);
  const isHydrating = useMapStore((state) => state.isHydrating);

  const [title, setTitle] = useState<string>(null);
  const [sort, setSort] = useState<any>({ field: 'Name', ascending: true });
  const [search, setSearch] = useState<string>('');
  const history = useHistory();

  // Diff saved vs map items for color coding
  const getListItems = (listFeatureCol, mapFeatureCol) => {
    let listIds = listFeatureCol.features.map((feature) => {
      return {
        Id: feature.properties.Id,
        Name: feature.properties.Name,
        LastCheckIn: feature.properties.LastCheckIn,
        EntityType: feature.properties.EntityType,
      };
    });
    let mapIds = mapFeatureCol.features.map((feature) => {
      return {
        Id: feature.properties.Id,
        Name: feature.properties.Name,
        LastCheckIn: feature.properties.LastCheckIn,
        EntityType: feature.properties.EntityType,
      };
    });
    const deletedItems = listIds.filter((item) => !mapIds.map((x) => x.Id).includes(item.Id));
    const newItems = mapIds.filter((item) => !listIds.map((x) => x.Id).includes(item.Id));
    const existingItems = listIds.filter((item) => mapIds.map((x) => x.Id).includes(item.Id));
    deletedItems.forEach((item) => {
      item.color = 0;
    });
    newItems.forEach((item) => {
      item.color = 1;
    });
    existingItems.forEach((item) => {
      item.color = 2;
    });
    const allItems = [...newItems, ...deletedItems, ...existingItems];

    return allItems;
  };

  // Local search/sort
  const listItems = useMemo(() => {
    if (!data || !listData) return [];

    let items = getListItems(listData, data);

    if (search?.trim()) {
      const q = search.trim().toLowerCase();
      items = items.filter((item) => item.Name?.toLowerCase().includes(q));
    }

    items = [...items].sort((a, b) => {
      const aVal = a[sort.field] ?? '';
      const bVal = b[sort.field] ?? '';
      if (aVal < bVal) return sort.ascending ? -1 : 1;
      if (aVal > bVal) return sort.ascending ? 1 : -1;
      return 0;
    });

    return items;
  }, [data, listData, search, sort]);

  useEffect(() => {
    if (type) {
      type === 'stationary' ? setTitle('Stationary Assets') : setTitle(capitalize(type) + 's');
    } else {
      setTitle(null);
    }
  }, [type]);

  // Add/remove markers from cluster group based on search match
  useEffect(() => {
    if (!listMarkers || !listRefs) return;

    const searchActive = !!search?.trim();
    const q = search.trim().toLowerCase();

    Object.values(listRefs).forEach((marker: any) => {
      const name = marker?.feature?.properties?.Name;
      const matches = !searchActive || name?.toLowerCase().includes(q);
      const isOnMap = listMarkers.hasLayer(marker);

      if (matches && !isOnMap) {
        listMarkers.addLayer(marker);
      } else if (!matches && isOnMap) {
        listMarkers.removeLayer(marker);
      }
    });
  }, [search, listMarkers, listRefs]);

  const fireClickEvent = (marker) => {
    if (type === 'asset' || type === 'gateway' || type === 'stationary') {
      var visibleLayer = data.getVisibleParent(marker);
      if (visibleLayer instanceof L.MarkerCluster) {
        data.zoomToShowLayer(marker, () => {
          marker.fire('click');
        });
      } else {
        marker.fire('click');
      }
    } else {
      marker.fire('click');
    }
  };

  const toggleMarkerShadow = (layer: L.Layer, show: boolean) => {
    if (!layer) return;

    if (layer instanceof L.Marker) {
      const el = layer.getElement?.();
      if (!el) return;
      const container = el.querySelector('.marker-container');
      if (!container) return;
      container.classList.toggle('has-shadow', show);
    } else if (layer instanceof L.MarkerCluster) {
      const el = layer._icon;
      if (!el) return;
      const container = el.querySelector('.marker-container');
      if (!container) return;
      container.classList.toggle('has-shadow', show);
    }
  };

  const fireMouseOverEvent = (layer: L.Layer) => {
    if (!layer) return;

    if (layer instanceof L.Path) {
      layer.fire('mouseenter');
      return;
    }

    const visibleLayer =
      typeof (listMarkers as any)?.getVisibleParent === 'function'
        ? (listMarkers as any).getVisibleParent(layer) || layer
        : layer;

    toggleMarkerShadow(visibleLayer, true);
  };

  const fireMouseOutEvent = (layer: L.Layer) => {
    if (!layer) return;

    if (layer instanceof L.Path) {
      layer.fire('mouseout');
      return;
    }

    const visibleLayer =
      typeof (listMarkers as any)?.getVisibleParent === 'function'
        ? (listMarkers as any).getVisibleParent(layer) || layer
        : layer;

    toggleMarkerShadow(visibleLayer, false);
  };

  return (
    <div className={visible ? 'object-wrapper-edit' : 'object-wrapper-none'}>
      <div className={visible ? 'pinned-controls-edit' : 'pinned-controls-none'}>
        <div className="d-flex flex-row align-items-center justify-content-between">
          <InputGroup>
            <div className="d-flex flex-row">
              <Text className="title-text fw-semibold fs-md me-3">{title ? title : ''}</Text>
            </div>
            <Form.Control
              id={'Map Search'}
              className="map-search"
              autoComplete="off"
              placeholder="Search by Name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Icon
              name={'add'}
              title="Close"
              className="sonarIcon-rotate-45 align-sub nav-text-icon"
              variant="light"
              disabled={false}
              size="md"
              showPointer={true}
              onClick={() => {
                setSelected(null);
              }}
            />
          </InputGroup>
        </div>
        {type !== 'overlay' && (
          <div className="d-flex flex-row align-items-center justify-content-between"></div>
        )}
        {!isHydrating && (
          <div className="d-flex flex-row align-items-center justify-content-between title-row mt-3">
            <div className="d-flex align-items-center justify-content-between">
              <Text className="title-text fw-semibold fs-md me-2">Create New</Text>
              <Icon
                name={'add'}
                title={`Add ${capitalize(type)}`}
                className="align-sub primary-icon"
                variant="light"
                disabled={false}
                size="md"
                onClick={() => {
                  setSearch('');
                  setIsCreating(true);
                }}
              />
            </div>
          </div>
        )}
      </div>
      {listData?.features?.length > 0 && (
        <div className="list-container-edit">
          <div key={'headers'}>
            <div className="header-btns">
              <div className="name-col">
                <Button
                  title="Sort by Name"
                  className="btn-light d-flex justify-content-between align-items-center nav-text-icon me-3"
                  variant="light"
                  onClick={() => {
                    if (sort.field !== 'Name') {
                      setSort({ field: 'Name', ascending: true });
                    } else {
                      sort.ascending
                        ? setSort({ field: 'Name', ascending: false })
                        : setSort({ field: 'Name', ascending: true });
                    }
                  }}
                >
                  Name &nbsp;
                  {sort.field === 'Name' && (
                    <i
                      className={
                        sort.ascending
                          ? 'fa fa-arrow-up fs-sm fw-lighter'
                          : 'fa fa-arrow-down fs-sm fw-lighter'
                      }
                    />
                  )}
                </Button>
              </div>
              {type !== 'place' && (
                <div className="checkin-col">
                  <Button
                    title="Sort by Name"
                    className="btn-light d-flex justify-content-between align-items-center nav-text-icon me-3"
                    variant="light"
                    onClick={() => {
                      if (sort.field !== 'LastCheckIn') {
                        setSort({ field: 'LastCheckIn', ascending: true });
                      } else {
                        sort.ascending
                          ? setSort({ field: 'LastCheckIn', ascending: false })
                          : setSort({ field: 'LastCheckIn', ascending: true });
                      }
                    }}
                  >
                    Last Check-In &nbsp;
                    {sort.field === 'LastCheckIn' && (
                      <i
                        className={
                          sort.ascending
                            ? 'fa fa-arrow-up fs-sm fw-lighter'
                            : 'fa fa-arrow-down fs-sm fw-lighter'
                        }
                      />
                    )}
                  </Button>
                </div>
              )}
              <div className="btns-col"></div>
            </div>
          </div>
          <>
            {listItems?.map((feature, index) => {
              return (
                <div
                  id={feature.Id}
                  key={index}
                  onMouseOver={() => {
                    try {
                      let marker = listRefs[feature?.Id];
                      fireMouseOverEvent(marker);
                    } catch (error) {
                      console.log(error);
                    }
                  }}
                  onMouseOut={() => {
                    try {
                      let marker = listRefs[feature?.Id];
                      fireMouseOutEvent(marker);
                    } catch (error) {
                      console.log(error);
                    }
                  }}
                  onClick={() => {
                    try {
                      let marker = listRefs[feature?.Id];
                      fireClickEvent(marker);
                    } catch (error) {
                      console.log(error);
                    }
                  }}
                >
                  <div
                    className={
                      feature.color === 0
                        ? 'deleted-item'
                        : feature.color === 1
                        ? 'new-item'
                        : 'list-item'
                    }
                  >
                    <div className="list-info">
                      <div className="name-col">{feature.Name}</div>
                      {feature.LastCheckIn && (
                        <div className="checkin-col">
                          {getFormattedDateTime(feature.LastCheckIn)}
                        </div>
                      )}
                    </div>
                    <div className="btns-col">
                      {(type === 'asset' || type === 'stationary') && (
                        <Button
                          title="Go to Asset"
                          className="btn-light d-flex justify-content-between align-items-center nav-text-icon"
                          variant="light"
                          onClick={(e: React.MouseEvent<HTMLElement>) => {
                            history.push('/assets/' + feature.Id);
                            e.stopPropagation();
                          }}
                        >
                          <i className={`sonarIcon sonarIcon-open-in fs-md`} />
                        </Button>
                      )}
                      <Button
                        title="Show more"
                        className="btn-light d-flex justify-content-between align-items-center nav-text-icon"
                        variant="light"
                        id={`more-btn-${feature?.Id}`}
                        disabled={feature.color === 0 || feature.color === 1}
                        onClick={(e: React.MouseEvent<HTMLElement>) => {
                          let newData = Object.values(
                            listMarkers._featureGroup?._layers ?? listMarkers._layers,
                          );

                          setShowMore(
                            listData?.features?.find((item) => item.properties.Id === feature.Id),
                          );

                          let result: any = null;
                          for (const item of newData) {
                            if (item instanceof L.MarkerCluster) {
                              const child = (item as any)
                                .getAllChildMarkers()
                                .find(
                                  (child) =>
                                    (child as any).feature?.properties?.Id?.toString() ===
                                    feature.Id.toString(),
                                );
                              if (child) {
                                result = child;
                                break;
                              }
                            } else if (
                              (item as any).feature?.properties?.Id?.toString() ===
                              feature.Id.toString()
                            ) {
                              result = item;
                              break;
                            }
                          }
                          setClickedEvent(result);
                          e.stopPropagation();
                        }}
                      >
                        <i className={`sonarIcon sonarIcon-h-dots-1 fs-md`} />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        </div>
      )}
    </div>
  );
}
